import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { supabase } from "../../lib/supabase";
import "./Events.css";

const DEFAULT_STORIES = [
  { id: 1, title: "Sudan's War Scatters Country's Emergent Art Scene", date: "June 1, 2023 · Reuters", img: "https://static01.nyt.com/images/2023/05/28/multimedia/28sudan-artists-1-mpjf/28sudan-artists-1-mpjf-superJumbo.jpg?quality=75&auto=webp", body: "Painter Yasmeen Abdullah had to leave much behind when she fled the war erupting around her. \"I had to abandon many things — about 20 pieces of artwork, years of artistic practice, rough sketches, drawings – literally everything.\"", tag: "War & Art" },
  { id: 2, title: "A Dark Page — Affordable Art Exhibition", date: "2023 · SPAR Hub", img: "https://images.csmonitor.com/csm/2023/07/0706-SUDANART-artist-LEDE.jpg?alias=standard_900x600nc", body: "As a group of artists, we decided not to give up to frustration. In this case, frustration would mean defeat — and we are not willing to be defeated.", tag: "Exhibition" },
  { id: 3, title: "Narrating Our Own Story: Amid War, Art Lovers Take a Stand", date: "July 2023 · Christian Science Monitor", img: "https://images.csmonitor.com/csm/2023/07/0706-SUDANART-mural.jpg?alias=standard_900x600", body: "Afraa Ahmed left with nothing. When the Sudanese artist fled her home in Khartoum in late April, bombs were already falling on her neighborhood.", tag: "Resilience" },
  { id: 4, title: "How Art Helped Propel Sudan's Revolution", date: "2019 · SPAR Archive", img: "https://www.albawaba.com/sites/default/files/im_new/ewelina/000_1FW1G5.jpg", body: "During Sudan's 2019 revolution — as people mobilized across the country — artists helped capture the country's discontent and solidify protesters' resolve.", tag: "Revolution" },
  { id: 5, title: "Dara Art Gallery: Art Is a Language", date: "2023 · Downtown Gallery", img: "https://cdn.cloud.prio.org/images/54394d4b4c2546ee948712be3d1d540e.png?x=1500&y=1000&m=AutomaticCover", body: "\"Artists are not just nice to have — they've been an integral part of the framing of the narratives of the democracy movement.\"", tag: "Gallery" },
  { id: 6, title: "Mohamed Bahnas — Sudanese Poet and Artist Dies Homeless in Cairo", date: "December 20, 2013 · ArabLit", img: "https://i0.wp.com/arablit.org/wp-content/uploads/2013/12/bahnas.jpg", body: "Multitalented Mohamed Hussein Bahnas — guitarist, singer, artist, poet, and novelist — died of hunger and exposure on Cairo's streets at age 42.", tag: "In Memoriam" },
];

const TAG_COLORS = {
  "War & Art":   { bg: "rgba(192,57,43,0.10)",  color: "#C0392B" },
  "Exhibition":  { bg: "rgba(30,86,49,0.10)",   color: "#1E5631" },
  "Resilience":  { bg: "rgba(201,150,12,0.12)", color: "#9A7D0A" },
  "Revolution":  { bg: "rgba(192,57,43,0.10)",  color: "#C0392B" },
  "Gallery":     { bg: "rgba(30,86,49,0.10)",   color: "#1E5631" },
  "In Memoriam": { bg: "rgba(0,0,0,0.08)",      color: "#555" },
};

function StoryTag({ tag }) {
  return <span className="ev-tag" style={TAG_COLORS[tag] || {}}>{tag}</span>;
}

function Lightbox({ src, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.92)",
      display: "flex", alignItems: "center", justifyContent: "center", cursor: "zoom-out",
    }}>
      <img src={src} alt="" style={{ maxWidth: "92vw", maxHeight: "92vh", borderRadius: 8, objectFit: "contain" }} />
      <button onClick={onClose} style={{ position: "absolute", top: 20, right: 24, background: "none", border: "none", color: "#fff", fontSize: 28, cursor: "pointer" }}>✕</button>
    </div>
  );
}

function FeatureCard({ story }) {
  const [imgErr, setImgErr] = useState(false);
  return (
    <article className="ev-story">
      {!imgErr ? (
        <div className="ev-story-img-wrap">
          <img src={story.img} alt={story.title} className="ev-story-img" onError={() => setImgErr(true)} />
          <StoryTag tag={story.tag} />
        </div>
      ) : (
        <div className="ev-story-img-wrap ev-story-img-fallback"><StoryTag tag={story.tag} /></div>
      )}
      <div className="ev-story-body">
        <span className="ev-story-date">{story.date}</span>
        <h3 className="ev-story-title">{story.title}</h3>
        <p className="ev-story-text">{story.body}</p>
      </div>
    </article>
  );
}

function NewsCard({ article }) {
  const [imgErr, setImgErr] = useState(false);
  return (
    <a className="ev-news-card" href={article.link || "#!"} target="_blank" rel="noopener noreferrer">
      <div className="ev-news-img-wrap">
        {article.image_url && !imgErr ? (
          <img src={article.image_url} alt={article.title} className="ev-news-img" onError={() => setImgErr(true)} />
        ) : (
          <div className="ev-news-img ev-news-img-placeholder">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.35">
              <rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
            </svg>
          </div>
        )}
      </div>
      <div className="ev-news-body">
        <div className="ev-news-meta">
          <span className="ev-news-source">{article.source_id}</span>
          <span className="ev-news-date">{article.pubDate?.slice(0, 10) || ""}</span>
        </div>
        <h4 className="ev-news-title">{article.title}</h4>
        {article.description && (
          <p className="ev-news-desc">{article.description.length > 120 ? article.description.slice(0, 120) + "…" : article.description}</p>
        )}
      </div>
    </a>
  );
}

export default function Events() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar" || i18n.language?.startsWith("ar");
  const [cms,          setCms]          = useState({});
  const [news,         setNews]         = useState([]);
  const [newsLoading,  setNewsLoading]  = useState(true);
  const [newsErr,      setNewsErr]      = useState(false);
  const [activeStory,  setActiveStory]  = useState(0);
  const [lightbox,     setLightbox]     = useState(null);
  const openLb = useCallback((src) => setLightbox(src), []);

  useEffect(() => {
    supabase
      .from("page_sections")
      .select("*")
      .eq("page", "events")
      .eq("is_visible", true)
      .order("sort_order", { ascending: true })
      .then(({ data }) => {
        if (data) {
          const map = {};
          data.forEach((s) => { map[s.section_key] = s; });
          setCms(map);
        }
      });

    axios
      .get("https://newsdata.io/api/1/news?country=sd&language=en,ar&apikey=pub_265077d47c856ccb5511a0dc1d09a0fcdd41c")
      .then((res) => setNews(res.data?.results || []))
      .catch(() => setNewsErr(true))
      .finally(() => setNewsLoading(false));
  }, []);

  const cTitle = (key, defEn, defAr) => {
    const s = cms[key];
    if (!s) return isAr ? (defAr || defEn) : defEn;
    return isAr ? (s.title_ar || s.title_en || defAr || defEn) : (s.title_en || s.title_ar || defEn);
  };
  const cBody = (key, defEn, defAr) => {
    const s = cms[key];
    if (!s) return isAr ? (defAr || defEn) : defEn;
    return isAr ? (s.body_ar || s.body_en || defAr || defEn) : (s.body_en || s.body_ar || defEn);
  };

  // Stories: CMS story_* entries or DEFAULT_STORIES fallback
  const stories = (() => {
    const items = Object.values(cms)
      .filter((s) => s.section_key.startsWith("story_"))
      .sort((a, b) => a.sort_order - b.sort_order);
    if (items.length > 0) return items.map((s) => ({
      id:    s.id,
      title: isAr ? (s.title_ar || s.title_en) : (s.title_en || s.title_ar),
      date:  s.image_caption || "",
      img:   s.image_url || "",
      body:  isAr ? (s.body_ar || s.body_en) : (s.body_en || s.body_ar),
      tag:   s.link_label_en || "Story",
      clickable: s.image_clickable,
    }));
    return DEFAULT_STORIES;
  })();

  const featured   = stories[activeStory] || stories[0];
  const thumbnails = stories.filter((_, i) => i !== activeStory).slice(0, 4);

  // Extra non-story CMS sections
  const HANDLED = new Set(["header", "rakoba", "dabanga_rss"]);
  const extraSections = Object.values(cms)
    .filter((s) => !HANDLED.has(s.section_key) && !s.section_key.startsWith("story_"))
    .sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="ev-page" dir={isAr ? "rtl" : "ltr"}>

      {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}

      {/* ── Page title ──────────────────────────────────── */}
      <header className="ev-page-header">
        <div className="ev-page-header-inner">
          <p className="ev-eyebrow">{isAr ? "أخبار وفعاليات" : "News & Events"}</p>
          <h1 className="ev-page-title">
            {cTitle("header", "Art Events & Activities", "الفعاليات والأنشطة الفنية")}
          </h1>
          <p className="ev-page-subtitle">
            {cBody("header",
              "Stories of Sudanese art — resilience, revolution, and beauty from our community.",
              "قصص الفن السوداني — الصمود والثورة والجمال من مجتمعنا."
            )}
          </p>
        </div>
      </header>

      {/* ── Featured story split layout ─────────────────── */}
      <section className="ev-section">
        <div className="ev-feature-layout">
          <div className="ev-feature-main">
            <div className="ev-feature-img-wrap">
              <img
                src={featured.img} alt={featured.title} className="ev-feature-img"
                onClick={featured.clickable ? () => openLb(featured.img) : undefined}
                style={{ cursor: featured.clickable ? "zoom-in" : "default" }}
                onError={(e) => { e.target.style.display = "none"; }}
              />
              <div className="ev-feature-overlay">
                <StoryTag tag={featured.tag} />
                <h2 className="ev-feature-title">{featured.title}</h2>
                <p className="ev-feature-date">{featured.date}</p>
              </div>
            </div>
            <div className="ev-feature-body">
              <p className="ev-feature-text">{featured.body}</p>
            </div>
          </div>
          <aside className="ev-feature-sidebar">
            <p className="ev-sidebar-label">{isAr ? "قصص أخرى" : "More Stories"}</p>
            {thumbnails.map((s) => (
              <button
                key={s.id}
                className="ev-thumb"
                onClick={() => setActiveStory(stories.findIndex((x) => x.id === s.id))}
              >
                <img src={s.img} alt={s.title} className="ev-thumb-img" onError={(e) => { e.target.style.display = "none"; }} />
                <div className="ev-thumb-body">
                  <StoryTag tag={s.tag} />
                  <p className="ev-thumb-title">{s.title}</p>
                  <span className="ev-thumb-date">{s.date}</span>
                </div>
              </button>
            ))}
          </aside>
        </div>
      </section>

      {/* ── All stories grid ────────────────────────────── */}
      <section className="ev-section">
        <h2 className="spar-section-title">{isAr ? "جميع القصص" : "All Stories"}</h2>
        <div className="ev-stories-grid">
          {stories.map((story) => <FeatureCard key={story.id} story={story} />)}
        </div>
      </section>

      {/* ── Live news from API ──────────────────────────── */}
      <section className="ev-section">
        <div className="ev-section-head">
          <h2 className="spar-section-title">{isAr ? "أخبار السودان الحية" : "Live Sudan News"}</h2>
          <span className="ev-live-dot" aria-label="Live"><span />Live</span>
        </div>
        {newsLoading && <div className="ev-news-loading"><div className="ev-spinner" /><span>{isAr ? "جارٍ التحميل..." : "Loading latest news…"}</span></div>}
        {newsErr && !newsLoading && <div className="ev-news-error">{isAr ? "تعذّر تحميل الأخبار." : "Could not load live news."}</div>}
        {!newsLoading && !newsErr && news.length === 0 && <div className="ev-news-error">{isAr ? "لا توجد نتائج." : "No news results returned."}</div>}
        {!newsLoading && news.length > 0 && (
          <div className="ev-news-grid">
            {news.slice(0, 12).map((article, i) => <NewsCard key={article.article_id || i} article={article} />)}
          </div>
        )}
      </section>

      {/* ── RSS Feeds ───────────────────────────────────── */}
      <section className="ev-section">
        <h2 className="spar-section-title">
          {cTitle("rakoba", "صحيفة الراكوبة — Rakoba Daily", "صحيفة الراكوبة")}
        </h2>
        <p className="ev-feed-desc">
          {cBody("rakoba",
            "Sudan's leading Arabic-language news source — covering art, culture, and conflict.",
            "المصدر الإخباري الرائد باللغة العربية في السودان — يغطي الفن والثقافة والصراع."
          )}
        </p>
        <div className="ev-rss-frame-wrap">
          <iframe src="https://rss.app/embed/v1/magazine/X2hAhq3EIhQuEi5u" title="Rakoba RSS Magazine" className="ev-rss-frame" frameBorder="0" />
        </div>
      </section>

      <section className="ev-section">
        <h2 className="spar-section-title">
          {cTitle("dabanga_rss", "Dabanga Radio TV Online", "دبنقا راديو تي في أونلاين")}
        </h2>
        <p className="ev-feed-desc">
          {cBody("dabanga_rss",
            "Independent news from Sudan — on-the-ground reporting in English and Arabic.",
            "أخبار مستقلة من السودان — تغطية ميدانية بالإنجليزية والعربية."
          )}
        </p>
        <div className="ev-rss-frame-wrap">
          <iframe src="https://rss.app/embed/v1/magazine/JaZymya66YZQnBXU" title="Dabanga RSS Magazine" className="ev-rss-frame" frameBorder="0" />
        </div>
      </section>

      {/* ── Extra CMS sections ──────────────────────────── */}
      {extraSections.map((s) => {
        const title    = isAr ? (s.title_ar || s.title_en) : (s.title_en || s.title_ar);
        const body     = isAr ? (s.body_ar  || s.body_en)  : (s.body_en  || s.body_ar);
        const btnLabel = isAr ? (s.link_label_ar || s.link_label_en) : (s.link_label_en || s.link_label_ar);
        return (
          <section key={s.id} className="ev-section">
            {title && <h2 className="spar-section-title">{title}</h2>}
            {s.image_url && (
              <img src={s.image_url} alt={title || ""}
                onClick={s.image_clickable ? () => openLb(s.image_url) : undefined}
                onError={(e) => { e.target.style.display = "none"; }}
                style={{ width: "100%", borderRadius: 12, marginBottom: 20, objectFit: "cover", cursor: s.image_clickable ? "zoom-in" : "default" }} />
            )}
            {body && body.split("\n\n").filter(Boolean).map((p, i) => <p key={i} className="ev-feature-text">{p}</p>)}
            {s.link_url && (
              <a href={s.link_url} target={s.link_target || "_self"}
                 rel={s.link_target === "_blank" ? "noopener noreferrer" : undefined}
                 className="spar-btn spar-btn-primary" style={{ marginTop: 12 }}>
                {btnLabel || (isAr ? "اقرأ المزيد" : "Learn More")}
              </a>
            )}
          </section>
        );
      })}

    </div>
  );
}
